import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, MapPin, Briefcase, Clock, Loader2 } from "lucide-react";
import { CAREERS } from "../data";
import { publicCareers } from "../lib/api";
import Logo from "../components/vyft/Logo";
import ApplyDialog from "../components/vyft/ApplyDialog";
import { Reveal, Overline } from "../components/vyft/primitives";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

export default function RoleDetail() {
  const { roleId } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState(undefined); // undefined=loading, null=not found

  useEffect(() => {
    document.documentElement.classList.add("dark");
    window.scrollTo(0, 0);
    publicCareers()
      .then((list) => {
        const found = (Array.isArray(list) ? list : []).find((r) => r.id === roleId);
        if (found) setRole(found);
        else {
          const fallback = CAREERS.find((r) => r.id === roleId);
          setRole(fallback || null);
        }
      })
      .catch(() => setRole(CAREERS.find((r) => r.id === roleId) || null));
  }, [roleId]);

  if (role === undefined) {
    return (
      <div className="min-h-screen grid place-items-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!role) {
    return (
      <div className="min-h-screen grid place-items-center bg-background text-foreground px-6 text-center">
        <div>
          <h1 className="font-display text-3xl font-bold">Role not found</h1>
          <Button onClick={() => navigate("/careers")} className="mt-6 rounded-full bg-brand-gradient text-white border-0">View all roles</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="fixed top-0 inset-x-0 z-50 px-4 pt-4">
        <div className="mx-auto max-w-7xl flex items-center justify-between rounded-full px-4 md:px-6 h-16 backdrop-blur-xl bg-background/70 border border-border/10">
          <button data-testid="role-logo" onClick={() => navigate("/")} className="flex items-center gap-2"><Logo /></button>
          <div className="hidden sm:inline-flex"><ApplyDialog role={role} /></div>
        </div>
      </header>

      <section className="relative pt-32 pb-16 px-6 overflow-hidden grain">
        <div className="orb bg-[#FF1F8E] h-96 w-96 -top-32 -left-24" />
        <div className="orb bg-[#F97316] h-80 w-80 top-10 right-0" />
        <div className="relative z-10 mx-auto max-w-4xl">
          <button data-testid="role-back" onClick={() => navigate("/careers")} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
            <ArrowLeft className="h-4 w-4" /> All open roles
          </button>
          <div className="mt-8 flex items-center gap-3"><Overline>Careers at VYFT</Overline><Badge variant="outline" className="border-primary/30 text-primary">{role.dept}</Badge></div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]"
          >
            {role.title}
          </motion.h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground leading-relaxed">{role.summary}</p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Briefcase className="h-4 w-4" /> {role.dept}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" /> {role.type}</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {role.location}</span>
          </div>
          <div className="mt-8"><ApplyDialog role={role} /></div>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-6 pb-8 grid md:grid-cols-2 gap-10">
        <Reveal>
          <Overline>What you'll do</Overline>
          <ul className="mt-5 space-y-3">
            {role.responsibilities.map((r) => (
              <li key={r} className="flex items-start gap-3 text-muted-foreground leading-relaxed"><span className="mt-1 h-6 w-6 shrink-0 grid place-items-center rounded-full bg-brand-gradient text-white"><Check className="h-3.5 w-3.5" /></span> {r}</li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.1}>
          <Overline>What we're looking for</Overline>
          <ul className="mt-5 space-y-3">
            {role.requirements.map((r) => (
              <li key={r} className="flex items-start gap-3 text-muted-foreground leading-relaxed"><span className="mt-1 h-6 w-6 shrink-0 grid place-items-center rounded-full bg-brand-gradient text-white"><Check className="h-3.5 w-3.5" /></span> {r}</li>
            ))}
          </ul>
        </Reveal>
      </div>

      <section className="mt-12 px-6 pb-24">
        <div className="mx-auto max-w-4xl rounded-[2rem] bg-brand-gradient p-10 md:p-14 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-extrabold text-white tracking-tight">Sound like you?</h2>
          <p className="mt-3 text-white/85 max-w-xl mx-auto">Send your details and resume — we read every application.</p>
          <div className="mt-8 inline-flex"><ApplyDialog role={role} light /></div>
        </div>
      </section>
    </div>
  );
}
